// Detailed content for TOK signature shows that have local detail pages.
// Each slug maps to /shows/:slug

import { TOK_ASSETS } from "./data";

export const SHOWS = {
  "windrush-immersive-experience": {
    slug: "windrush-immersive-experience",
    title: "The Windrush Immersive Experience",
    tagline: "A journey through memory, movement and legacy.",
    eyebrow: "Featured Immersive Experience",
    status: "On sale — pre-booking required",
    runDates: "22 June — 26 June 2026",
    runDatesShort: "22–26 June",
    venue: "HYPHA Studio",
    venueAddress: "2 Walter Burke Way, Chatham ME4 4RQ",
    runTime: "Approx. 75 minutes",
    language: "English",
    ageGuidance: "12+ recommended",
    heroImage: TOK_ASSETS.windrushBanner,
    posterImage: TOK_ASSETS.windrushIllustration,
    socialImage: TOK_ASSETS.windrushFacebook,
    gallery: [
      TOK_ASSETS.windrushIllustration,
      TOK_ASSETS.windrushSpaceTop,
      TOK_ASSETS.windrushFacebook,
      TOK_ASSETS.windrushBanner,
    ],
    synopsis: [
      "The Windrush Immersive Experience is Temple of Kulture's most personal commission to date — a journey through memory, movement and legacy that places audiences inside the lived inheritance of the Windrush generation.",
      "Stepping into HYPHA Studio, you'll travel from the deck of the Empire Windrush in 1948 through dance halls, front rooms, sound systems and the streets where a generation built a new home — and a new culture — out of resilience and joy.",
      "Combining XR theatre, projection mapping, original sound design and live performance, the experience honours those who arrived, those who stayed, and those who carry the memory forward.",
    ],
    themes: [
      "Memory & ancestry",
      "Migration & arrival",
      "Movement, music & dance",
      "Legacy of the global majority",
      "Sound system culture",
      "Home-building & belonging",
    ],
    sections: [
      {
        h: "Arrival",
        b: "An immersive opening sequence — the journey across the Atlantic, projected and sounded in 360°.",
      },
      {
        h: "Front room",
        b: "Step into the intimate spaces where a generation made home. Dominoes, records, vinyl, photographs, light.",
      },
      {
        h: "Sound system",
        b: "The streets and dance halls where Caribbean culture became British culture. A full-body sound design moment.",
      },
      {
        h: "Legacy",
        b: "An XR portrait gallery of contemporary descendants — your story projected alongside ancestral memory.",
      },
    ],
    accessibility: [
      "Step-free access throughout HYPHA Studio",
      "Quiet decompression room available before & after the experience",
      "BSL-interpreted slots on request — please email at least 14 days ahead",
      "Audio description available for the projection chambers",
      "Relaxed sessions scheduled across the run — see booking page",
    ],
    creditsHeading: "Created by Temple of Kulture",
    credits: [
      "Conceived & directed by the Temple of Kulture creative team",
      "Performed by an ensemble of UK & diasporic artists",
      "XR & projection design by Kulture Lab",
      "Sound design rooted in Caribbean sound system culture",
    ],
    bookingType: "external",
    bookingUrl: "https://www.templeofkulture.co.uk/contact",
    bookingCtaLabel: "Pre-book now",
    related: ["william-cuffay", "rebirth-of-a-mother"],
  },

  "william-cuffay": {
    slug: "william-cuffay",
    title: "William Cuffay",
    tagline: "Revolutionary. Chartist. Black British icon — finally told as immersive theatre.",
    eyebrow: "Living History",
    status: "In development — 2026 season",
    runDates: "2026 — exact dates announced soon",
    runDatesShort: "2026 season",
    venue: "Touring — Medway · London · Kent",
    venueAddress: "Venues TBA",
    runTime: "Approx. 90 minutes",
    language: "English",
    ageGuidance: "14+ recommended (contains references to imprisonment & political violence)",
    heroImage: TOK_ASSETS.williamCuffay,
    posterImage: TOK_ASSETS.williamCuffay,
    gallery: [
      TOK_ASSETS.williamCuffay,
      TOK_ASSETS.sqLiveTheatre,
      TOK_ASSETS.sqKulturalTheatre,
      TOK_ASSETS.sqPlaywriting,
    ],
    synopsis: [
      "Born in 1788 to a formerly enslaved father, William Cuffay rose to become a leader of the Chartist movement — the largest mass political movement in 19th-century Britain. He stood for working-class suffrage, fair pay and democratic reform, and was sentenced to transportation to Tasmania at sixty.",
      "Temple of Kulture's William Cuffay is the first immersive theatre piece dedicated to telling his story in full. Audiences walk through the meetings, the marches, the courtroom and the exile — placed inside the moments that history textbooks left out.",
      "Co-developed with historians and community researchers, the work uses XR, projection mapping and ensemble performance to return William Cuffay to the centre of the Black British story he helped write.",
    ],
    themes: [
      "Black British history",
      "Chartist movement & democracy",
      "Resistance & political memory",
      "Working-class solidarity",
      "Exile & return",
      "Untold leadership",
    ],
    sections: [
      {
        h: "The Tailor",
        b: "A young Cuffay at his workbench in 1830s London — the threads of community, craft and politics first weaving together.",
      },
      {
        h: "The Movement",
        b: "Inside the Chartist meetings of 1842 — projection-mapped onto a 360° chamber, audiences become part of the rally.",
      },
      {
        h: "The Trial",
        b: "An XR courtroom sequence drawn from historical record — the system on trial as much as the man.",
      },
      {
        h: "Exile & Legacy",
        b: "Tasmania, the diaspora, the descendants. A choral closing sequence written with contemporary poets.",
      },
    ],
    accessibility: [
      "Step-free venues prioritised across the tour",
      "Captioning available at all performances",
      "BSL-interpreted performances on request",
      "Pre-show research notes available for educators & schools",
      "Content guidance: contains references to political imprisonment",
    ],
    creditsHeading: "A Temple of Kulture original commission",
    credits: [
      "Original concept by Temple of Kulture",
      "Historical advisors from UK & Caribbean academic institutions",
      "Ensemble cast from the global majority",
      "Co-commissioned with cultural partners (to be announced)",
    ],
    bookingType: "enquiry",
    bookingUrl: "/corporate",
    bookingCtaLabel: "Register interest",
    related: ["windrush-immersive-experience", "rebirth-of-a-mother"],
  },

  "rebirth-of-a-mother": {
    slug: "rebirth-of-a-mother",
    title: "Rebirth of a Mother",
    tagline: "A meditation on grief, lineage and the women who carry whole worlds inside them.",
    eyebrow: "Immersive Show",
    status: "In development — 2026 season",
    runDates: "2026 — exact dates announced soon",
    runDatesShort: "2026 season",
    venue: "Medway · London (venue TBA)",
    venueAddress: "Venues TBA",
    runTime: "Approx. 60 minutes",
    language: "English with movement-led passages",
    ageGuidance: "16+ (themes of grief, loss & spiritual rebirth)",
    heroImage: TOK_ASSETS.rebirthMother,
    posterImage: TOK_ASSETS.rebirthMother,
    gallery: [
      TOK_ASSETS.rebirthMother,
      TOK_ASSETS.sqKulturalTheatre,
      TOK_ASSETS.sqLiveTheatre,
      TOK_ASSETS.sqXrImmersive,
    ],
    synopsis: [
      "A silhouette against a bonsai tree. A mother in the in-between. Rebirth of a Mother is a quiet, devastating immersive piece about what it means to remake yourself after loss — and how lineage flows backward as well as forward.",
      "Drawing on East Asian theatrical aesthetics, Caribbean spiritual tradition and contemporary movement, Temple of Kulture asks: when a mother is reborn, who else is reborn with her?",
      "Audiences enter a single chamber bathed in projection — half garden, half memory — for a 60-minute meditation built from sound, silhouette, slow movement and one extraordinary performer.",
    ],
    themes: [
      "Motherhood & lineage",
      "Grief & rebirth",
      "Spiritual tradition",
      "Movement-led storytelling",
      "Silence as language",
    ],
    sections: [
      {
        h: "The Garden",
        b: "A projection-mapped chamber that shifts from bonsai stillness to wild abundance across the performance.",
      },
      {
        h: "The Mother",
        b: "A single performer in silhouette, moving between presence and absence.",
      },
      {
        h: "The Ancestors",
        b: "Voices layered through the room — recorded testimonies from mothers across three generations.",
      },
      {
        h: "The Rebirth",
        b: "A closing ritual sequence the audience completes together.",
      },
    ],
    accessibility: [
      "Step-free access",
      "Quiet & low-stimulus by design — sensory-aware audiences welcome",
      "Content guidance: grief, loss, spiritual rebirth",
      "Translation cards available for non-English speakers",
    ],
    creditsHeading: "A Temple of Kulture original commission",
    credits: [
      "Concept & direction by Temple of Kulture",
      "Sound & projection design by Kulture Lab",
      "Movement collaboration with diaspora artists",
    ],
    bookingType: "enquiry",
    bookingUrl: "/corporate",
    bookingCtaLabel: "Register interest",
    related: ["windrush-immersive-experience", "william-cuffay"],
  },
};

export function getShow(slug) {
  return SHOWS[slug] || null;
}

export function getAllShows() {
  return Object.values(SHOWS);
}
